/**
 * Onboarding guide — the first minute in a walkable scene (Nov 2026).
 *
 * User tests showed first-time visitors did not understand the small
 * bottom card (controls-hint.ts): many had never been inside a 3D space on a
 * screen. This replaces it for walk scenes with three big steps, one at a
 * time, over the live room:
 *
 *   1  Click (tap) the floor to walk there   → a click actually glided
 *   2  Drag (swipe) to look around           → a real drag turned the view
 *   3  Scroll / two fingers to walk          → trackpad scroll / wheel /
 *                                              pinch / two fingers / WASD /
 *                                              joystick actually moved you
 *
 * Click comes first because it is the one thing every web user already
 * knows, it cannot go the wrong way, and it is the same on mouse, trackpad
 * and phone. Step 3 also teaches the walk controller which way this
 * machine's scrolling runs (startScrollCalibration): natural scrolling flips
 * the wheel's sign and a page cannot read that setting.
 *
 * Each step leaves when the visitor has DONE it (gesture flag set by the
 * controller's own input handlers AND the camera really moved), with a short
 * tick beat. "Next" moves on without doing it; "Skip tour" and Esc end it.
 * Finished or skipped, it is remembered on the device and does not start by
 * itself again; the toolbar's ? button replays it (mount.ts).
 *
 * The card sits at the top for step 1 (the floor must stay visible) and at
 * the bottom for the rest. A light dim keeps the room visible: people
 * practise on the real thing, never on a page of instructions.
 */

import type * as pc from 'playcanvas';

import type { WalkController } from '../controls/walk-controller';
import { resolveLang } from './lang';

const SEEN_KEY = 'olloport.guideDone';

/** Toolbar button that replays the guide (same 16px line style as the rest). */
export const ICON_HELP = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6.5" stroke="currentColor" stroke-width="1.2"/><path d="M6.2 6.2a1.9 1.9 0 1 1 2.6 1.75c-.5.22-.8.6-.8 1.15v.4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/><circle cx="8" cy="11.4" r=".75" fill="currentColor"/></svg>';

export const GUIDE_LABEL: Record<'en' | 'es' | 'ca', string> = {
    en: 'How to move around', es: 'Cómo moverte', ca: 'Com moure’t'
};

/** The name under the ? in the bottom dock. */
export const GUIDE_SHORT: Record<'en' | 'es' | 'ca', string> = {
    en: 'Help', es: 'Ayuda', ca: 'Ajuda'
};

type StepId = 'click' | 'look' | 'walk';

type Copy = {
    clickTitle: string; clickSub: string;
    tapTitle: string; tapSub: string;
    dragTitle: string; swipeTitle: string; lookSub: string;
    walkTitle: string; walkSub: string;
    walkTouchTitle: string; walkTouchSub: string;
    doneTitle: string; doneSub: string;
    next: string; skip: string; label: string;
};

// No em dashes in Olloport copy: periods, commas, colons.
const COPY: Record<'en' | 'es' | 'ca', Copy> = {
    en: {
        clickTitle: 'Click the floor to walk there',
        clickSub: 'The orange ring shows where you’ll land. Click a wall or a painting to walk up to it.',
        tapTitle: 'Tap the floor to walk there',
        tapSub: 'Tap a wall or a painting to walk up to it.',
        dragTitle: 'Drag to look around',
        swipeTitle: 'Swipe to look around',
        lookSub: 'Grab the room and pull it, like a photo.',
        walkTitle: 'Scroll to walk',
        walkSub: 'Roll the mouse wheel, or slide two fingers on the trackpad. Up walks forward, sideways steps aside. Pinch to get closer.',
        walkTouchTitle: 'Two fingers to walk',
        walkTouchSub: 'Slide two fingers up to walk forward. Spread them to get closer.',
        doneTitle: 'You’re all set',
        doneSub: 'Press ? at the bottom right to see this again.',
        next: 'Next', skip: 'Skip tour', label: 'How to move around'
    },
    es: {
        clickTitle: 'Haz clic en el suelo para ir allí',
        clickSub: 'El círculo naranja marca dónde llegarás. Haz clic en una pared o un cuadro para acercarte.',
        tapTitle: 'Toca el suelo para ir allí',
        tapSub: 'Toca una pared o un cuadro para acercarte.',
        dragTitle: 'Arrastra para mirar alrededor',
        swipeTitle: 'Desliza para mirar alrededor',
        lookSub: 'Agarra la sala y tira de ella, como una foto.',
        walkTitle: 'Haz scroll para caminar',
        walkSub: 'Gira la rueda del ratón o desliza dos dedos en el trackpad. Hacia arriba avanzas, de lado te desplazas. Pellizca para acercarte.',
        walkTouchTitle: 'Dos dedos para caminar',
        walkTouchSub: 'Desliza dos dedos hacia arriba para avanzar. Sepáralos para acercarte.',
        doneTitle: 'Ya está',
        doneSub: 'Pulsa ? abajo a la derecha para volver a verlo.',
        next: 'Siguiente', skip: 'Saltar', label: 'Cómo moverte'
    },
    ca: {
        clickTitle: 'Fes clic al terra per anar-hi',
        clickSub: 'El cercle taronja marca on arribaràs. Fes clic en una paret o un quadre per apropar-t’hi.',
        tapTitle: 'Toca el terra per anar-hi',
        tapSub: 'Toca una paret o un quadre per apropar-t’hi.',
        dragTitle: 'Arrossega per mirar al voltant',
        swipeTitle: 'Llisca per mirar al voltant',
        lookSub: 'Agafa la sala i estira-la, com una foto.',
        walkTitle: 'Fes scroll per caminar',
        walkSub: 'Gira la roda del ratolí o llisca dos dits al trackpad. Cap amunt avances, de costat et desplaces. Pessiga per apropar-te.',
        walkTouchTitle: 'Dos dits per caminar',
        walkTouchSub: 'Llisca dos dits cap amunt per avançar. Separa’ls per apropar-te.',
        doneTitle: 'Ja està',
        doneSub: 'Prem ? a baix a la dreta per tornar-ho a veure.',
        next: 'Següent', skip: 'Saltar', label: 'Com moure’t'
    }
};

// Big line glyphs, 72px, white on the dark card. The motion is the
// explanation; viewer.css animates the gd-anim-* groups and stops them under
// prefers-reduced-motion.
const svg = (inner: string) =>
    `<svg viewBox="0 0 96 72" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;

const CURSOR = '<path d="M0 0v22l6-5.5 4.5 9.5 4-2-4.5-9.3H18z" fill="#fff" stroke="#1c1814" stroke-width="1.6"/>';
const FINGER = '<circle r="7" fill="#fff" stroke="#1c1814" stroke-width="1.6"/>';
/** A mouse, 24 x 38, its top-left at (x, y): body, the two buttons, the
 *  wheel. `lit` paints the part the step is about in terracotta. */
const mouse = (x: number, y: number, lit: 'left' | 'wheel', wheelClass = '') =>
    `<g transform="translate(${x} ${y})">` +
    (lit === 'left' ? '<path d="M12 0A12 12 0 0 0 0 12v2h12z" fill="#c87048" stroke="none"/>' : '') +
    '<rect width="24" height="38" rx="12"/><path d="M12 0v14M0 14h24"/>' +
    `<rect class="${wheelClass}" x="10" y="4" width="4" height="7" rx="2" fill="${lit === 'wheel' ? '#c87048' : '#fff'}" stroke="none"/>` +
    '</g>';

/** Left and right arrows either side of the middle: "move it sideways". */
const SIDE_ARROWS = '<path d="M20 33h-10m0 0l4-4m-4 4l4 4M76 33h10m0 0l-4-4m4 4l-4 4" opacity=".7"/>';

const GLYPH = {
    click: (touch: boolean) => svg(
        '<ellipse class="gd-anim-ring" cx="48" cy="58" rx="26" ry="8" fill="#c87048" stroke="none"/>' +
        '<ellipse cx="48" cy="58" rx="9" ry="3" fill="#fff" stroke="none"/>' +
        '<path d="M48 10v32" stroke-dasharray="3 5" opacity=".55"/>' +
        `<g class="gd-anim-tap"><g transform="translate(${touch ? '48 30' : '47 24'})">${touch ? FINGER : CURSOR}</g></g>`
    ),
    // Hold the button and move sideways (a finger on touch), between arrows.
    look: (touch: boolean) => svg(
        SIDE_ARROWS +
        `<g class="gd-anim-pull">${touch ? '<g transform="translate(48 33)">' + FINGER + '</g>' : mouse(36, 14, 'left')}</g>`
    ),
    // Desktop: the wheel rolling up, or two fingers up on a trackpad, side
    // by side, since a page can't tell which one this computer has.
    walk: (touch: boolean) => svg(
        touch
            ? '<rect x="30" y="4" width="36" height="64" rx="7"/>' +
              '<g class="gd-anim-up"><circle cx="41" cy="46" r="5.5" fill="#c87048" stroke="none"/><circle cx="55" cy="46" r="5.5" fill="#c87048" stroke="none"/></g>' +
              '<path d="M48 22v-8m0 0l-5 5m5-5l5 5"/>'
            : mouse(14, 22, 'wheel', 'gd-anim-roll') +
              '<path d="M26 15V5m0 0l-4 4m4-4l4 4"/>' +
              '<rect x="52" y="22" width="34" height="38" rx="5"/>' +
              '<g class="gd-anim-up"><circle cx="63" cy="48" r="4.5" fill="#c87048" stroke="none"/><circle cx="75" cy="48" r="4.5" fill="#c87048" stroke="none"/></g>' +
              '<path d="M69 15V5m0 0l-4 4m4-4l4 4"/>'
    ),
    done: () => svg('<circle cx="48" cy="36" r="24" fill="#c87048" stroke="none"/><path d="M37 37l8 8 15-17" stroke="#fff" stroke-width="3.5"/>')
};

export interface OnboardingGuideOptions {
    parent: HTMLElement;
    walk: WalkController;
    camera: pc.Entity;
    /** Touch device: tap / swipe / phone wording and glyphs. */
    touch: boolean;
    /** Delay before the first step appears (ms): lets the reveal land. */
    delay?: number;
    /** Called once the guide is gone (finished, skipped or disposed). */
    onClose?: () => void;
}

export class OnboardingGuide {
    /** Has this device finished or skipped the guide before? `?guide=1`
     *  forces it (handy for testing), `?guide=0` suppresses it. */
    static wanted(): boolean {
        try {
            const q = new URLSearchParams(window.location.search).get('guide');
            if (q === '1') return true;
            if (q === '0') return false;
            return window.localStorage.getItem(SEEN_KEY) !== '1';
        } catch {
            return true;
        }
    }

    private readonly opts: OnboardingGuideOptions;
    private readonly t: Copy;
    private readonly root: HTMLDivElement;
    private readonly card: HTMLDivElement;
    private readonly glyphEl: HTMLDivElement;
    private readonly titleEl: HTMLDivElement;
    private readonly subEl: HTMLDivElement;
    private readonly dots: HTMLSpanElement[] = [];
    private readonly steps: StepId[] = ['click', 'look', 'walk'];
    private index = -1;
    private baseline: number[] = [];
    private timer: number | null = null;
    private pollTimer: number | null = null;
    private closed = false;
    private readonly onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') this.finish(); };

    constructor(opts: OnboardingGuideOptions) {
        this.opts = opts;
        this.t = COPY[resolveLang()];

        this.root = document.createElement('div');
        this.root.className = 'ollo-guide';
        this.root.innerHTML = '<div class="ollo-guide-dim"></div>';

        this.card = document.createElement('div');
        this.card.className = 'ollo-guide-card';
        this.card.setAttribute('role', 'dialog');
        this.card.setAttribute('aria-label', this.t.label);
        this.card.setAttribute('aria-live', 'polite');

        this.glyphEl = document.createElement('div');
        this.glyphEl.className = 'ollo-guide-glyph';
        this.titleEl = document.createElement('div');
        this.titleEl.className = 'ollo-guide-title';
        this.subEl = document.createElement('div');
        this.subEl.className = 'ollo-guide-sub';

        const foot = document.createElement('div');
        foot.className = 'ollo-guide-foot';
        const dotWrap = document.createElement('span');
        dotWrap.className = 'ollo-guide-dots';
        for (let i = 0; i < this.steps.length; i++) {
            const d = document.createElement('span');
            dotWrap.appendChild(d);
            this.dots.push(d);
        }
        const actions = document.createElement('span');
        actions.className = 'ollo-guide-actions';
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'ollo-guide-next';
        next.textContent = this.t.next;
        next.addEventListener('click', (e) => { e.stopPropagation(); this.advance(); });
        const skip = document.createElement('button');
        skip.type = 'button';
        skip.className = 'ollo-guide-skip';
        skip.textContent = this.t.skip;
        skip.addEventListener('click', (e) => { e.stopPropagation(); this.finish(); });
        actions.append(next, skip);
        foot.append(dotWrap, actions);

        this.card.append(this.glyphEl, this.titleEl, this.subEl, foot);
        this.root.appendChild(this.card);
        opts.parent.appendChild(this.root);
        window.addEventListener('keydown', this.onKey);

        this.timer = window.setTimeout(() => {
            if (this.closed) return;
            this.root.classList.add('visible');
            this.show(0);
        }, opts.delay ?? 1200);
    }

    private position(): number[] {
        const p = this.opts.camera.getPosition();
        const f = this.opts.camera.forward;
        return [p.x, p.z, Math.atan2(f.x, f.z), Math.asin(Math.max(-1, Math.min(1, f.y)))];
    }

    private show(i: number): void {
        if (this.closed) return;
        this.index = i;
        const step = this.steps[i];
        const { walk, touch } = this.opts;
        const t = this.t;
        // Reset the flag the step watches, so a gesture done BEFORE the step
        // (or the previous step's motion) can't tick it.
        if (step === 'click') walk.gestureClickMove = false;
        if (step === 'look') walk.gestureLook = false;
        if (step === 'walk') {
            walk.gestureWheel = false; walk.gesturePinch = false; walk.gestureKeys = false;
            walk.gestureTwoFinger = false; walk.gestureJoystick = false; walk.gestureHold = false;
            if (!touch) walk.startScrollCalibration();
        }
        this.baseline = this.position();

        this.card.classList.remove('done');
        this.card.classList.toggle('ollo-guide-card--top', step === 'click');
        this.card.classList.remove('enter');
        void this.card.offsetWidth;   // restart the enter animation
        this.card.classList.add('enter');

        if (step === 'click') {
            this.glyphEl.innerHTML = GLYPH.click(touch);
            this.titleEl.textContent = touch ? t.tapTitle : t.clickTitle;
            this.subEl.textContent = touch ? t.tapSub : t.clickSub;
        } else if (step === 'look') {
            this.glyphEl.innerHTML = GLYPH.look(touch);
            this.titleEl.textContent = touch ? t.swipeTitle : t.dragTitle;
            this.subEl.textContent = t.lookSub;
        } else {
            this.glyphEl.innerHTML = GLYPH.walk(touch);
            this.titleEl.textContent = touch ? t.walkTouchTitle : t.walkTitle;
            this.subEl.textContent = touch ? t.walkTouchSub : t.walkSub;
        }
        this.dots.forEach((d, k) => d.classList.toggle('on', k === i));
        this.dots.forEach((d, k) => d.classList.toggle('past', k < i));

        if (this.pollTimer !== null) clearInterval(this.pollTimer);
        this.pollTimer = window.setInterval(() => this.poll(), 200);
    }

    private poll(): void {
        const step = this.steps[this.index];
        const w = this.opts.walk;
        const now = this.position();
        const b = this.baseline;
        const moved = Math.hypot(now[0] - b[0], now[1] - b[1]);
        let yawD = Math.abs(now[2] - b[2]);
        if (yawD > Math.PI) yawD = 2 * Math.PI - yawD;
        const turned = Math.hypot(yawD, now[3] - b[3]);
        const ok =
            step === 'click' ? w.gestureClickMove && moved > 0.3 :
            step === 'look' ? w.gestureLook && turned > 0.15 :
            (w.gestureWheel || w.gesturePinch || w.gestureKeys || w.gestureTwoFinger ||
             w.gestureJoystick || w.gestureHold) && moved > 0.4;
        if (ok) this.complete();
    }

    /** The visitor did it: a tick, a beat, then the next step. */
    private complete(): void {
        if (this.pollTimer !== null) { clearInterval(this.pollTimer); this.pollTimer = null; }
        this.card.classList.add('done');
        this.glyphEl.innerHTML = GLYPH.done();
        this.timer = window.setTimeout(() => this.advance(), 900);
    }

    private advance(): void {
        if (this.closed) return;
        if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
        if (this.index + 1 < this.steps.length) {
            this.show(this.index + 1);
            return;
        }
        // All three done: a short "you're all set", then leave.
        if (this.pollTimer !== null) { clearInterval(this.pollTimer); this.pollTimer = null; }
        this.index = this.steps.length;
        this.card.classList.remove('ollo-guide-card--top');
        this.card.classList.add('done', 'last');
        this.glyphEl.innerHTML = GLYPH.done();
        this.titleEl.textContent = this.t.doneTitle;
        this.subEl.textContent = this.t.doneSub;
        this.dots.forEach(d => { d.classList.remove('on'); d.classList.add('past'); });
        this.timer = window.setTimeout(() => this.finish(), 2200);
    }

    /** Finished or skipped: remember it on this device and fade out. */
    finish(): void {
        if (this.closed) return;
        try { window.localStorage.setItem(SEEN_KEY, '1'); } catch { /* fine */ }
        this.close();
        window.setTimeout(() => this.root.remove(), 450);
    }

    private close(): void {
        this.closed = true;
        if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
        if (this.pollTimer !== null) { clearInterval(this.pollTimer); this.pollTimer = null; }
        window.removeEventListener('keydown', this.onKey);
        this.root.classList.remove('visible');
        this.opts.onClose?.();
    }

    dispose(): void {
        if (!this.closed) this.close();
        this.root.remove();
    }
}
